/**
 * Tasks — the inline composer's chips and its "Added for …" note (Tasks redesign, design "Tasks Page
 * Options" 2a: one input, chips below it while focused / typed in, Enter adds).
 *
 * Plan: C:\Users\nilay\.claude\plans\shiny-gliding-moth.md. Mirrors the design's compVM (chips),
 * dueLbl (the Due chip's value) and add() (the note under the composer when the new task is not in
 * the current view).
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE AND CLOCK-FREE: cypress/node-tests/tasks-composer-chips.test.js
 *    executes it verbatim, and tasks-my-day-rules.test.js guards every file in src/utils/tasks/.
 *    "Today" and every date arrive as 'YYYY-MM-DD' strings computed by the caller (TaskComposer.vue
 *    in the office zone; ToDo.vue from its myDay.js ctx).
 *
 * Callers: src/components/TaskComposer/TaskComposer.vue (variant="inline") and
 *          src/pages/TaskList/ToDo.vue (tkOnSaved → the note).
 */

const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAYS_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 86400000;

/** The view labels the note may point at (keys = myDay.js VIEWS keys). */
export const NOTE_VIEW_LABELS = {
  myDay: 'My day',
  assignedToMe: 'Assigned to me',
  createdByMe: 'Created by me',
  upcoming: 'Upcoming',
  everyone: 'Everyone',
};

function partsOf(ymd) {
  const m = typeof ymd === 'string' ? YMD_RE.exec(ymd) : null;
  return m ? { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) } : null;
}

function utcMs(ymd) {
  const p = partsOf(ymd);
  return p ? Date.UTC(p.y, p.m - 1, p.d) : null;
}

/**
 * Whole days from `fromYmd` to `toYmd` (negative when `toYmd` is earlier), or null.
 * @param {string} fromYmd 'YYYY-MM-DD'
 * @param {string} toYmd   'YYYY-MM-DD'
 * @returns {number|null}
 */
export function daysBetween(fromYmd, toYmd) {
  const a = utcMs(fromYmd);
  const b = utcMs(toYmd);
  if (a == null || b == null) return null;
  return Math.round((b - a) / DAY_MS);
}

/**
 * 'Oct 9' — or 'Oct 9, 2027' when the year differs from `todayYmd`'s. '' for a bad date.
 * @param {string} ymd       'YYYY-MM-DD'
 * @param {string} [todayYmd] 'YYYY-MM-DD'; omit to never show the year
 */
export function shortDate(ymd, todayYmd) {
  const p = partsOf(ymd);
  if (!p) return '';
  const t = partsOf(todayYmd);
  const base = `${MONTHS_SHORT[p.m - 1]} ${p.d}`;
  return t && t.y !== p.y ? `${base}, ${p.y}` : base;
}

/**
 * 'Fri, Oct 9' (the note's date). '' for a bad date.
 * @param {string} ymd 'YYYY-MM-DD'
 */
export function longDate(ymd) {
  const ms = utcMs(ymd);
  if (ms == null) return '';
  return `${WEEKDAYS_SHORT[new Date(ms).getUTCDay()]}, ${shortDate(ymd)}`;
}

// Leading courtesy titles are not a first name: 'Dr. Emily Carter' → 'Emily' (2026-10-21, Today greeted "Dr..").
const COURTESY_TITLES = ['dr', 'mr', 'mrs', 'ms', 'miss', 'mx', 'prof'];

/**
 * The first word of a name, after any leading courtesy title ('Priya Patel' → 'Priya', 'Dr. Emily Carter' → 'Emily');
 * a name that is only a title is returned as written; '' when blank.
 * @param {string} name
 */
export function firstName(name) {
  const words = String(name == null ? '' : name).trim().split(/\s+/).filter(Boolean);
  const first = words.find(w => !COURTESY_TITLES.includes(w.replace(/\.$/, '').toLowerCase()));
  return first || words[0] || '';
}

/** 'A' · 'A and B' · 'A, B and C'. */
function joinAnd(list) {
  const xs = list.filter(Boolean);
  if (xs.length <= 1) return xs.join('');
  return `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`;
}

/**
 * Assignees as [{ name, isMe }]. Accepts strings (a name; isMe when it equals `meName`) or objects
 * `{ name, isMe }` (the composer resolves isMe by EmployeeID, which is safer than by name).
 */
function normalizeAssignees(assigneeNames, meName) {
  const list = Array.isArray(assigneeNames) ? assigneeNames : [];
  const me = String(meName == null ? '' : meName).trim();
  return list
    .map((a) => {
      if (a && typeof a === 'object') return { name: String(a.name == null ? '' : a.name), isMe: !!a.isMe };
      const name = String(a == null ? '' : a);
      return { name, isMe: !!me && name.trim() === me };
    });
}

/** Chip / note text for one assignee: 'You' for me, else the first name ('Teammate' if unknown). */
function assigneeWord(a) {
  return a.isMe ? 'You' : (firstName(a.name) || 'Teammate');
}

/**
 * The Due chip's value: 'Today' · 'Tomorrow' · the weekday ('Friday') 2–6 days out · else 'Oct 9'
 * (also for past days). 'No date' when `ymd` is missing or not 'YYYY-MM-DD'.
 * @param {string} ymd      the due day, 'YYYY-MM-DD'
 * @param {string} todayYmd today in the office zone, 'YYYY-MM-DD'
 * @returns {string}
 */
export function dueChipLabel(ymd, todayYmd) {
  if (!partsOf(ymd)) return 'No date';
  const diff = daysBetween(todayYmd, ymd);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff != null && diff >= 2 && diff <= 6) return WEEKDAYS_FULL[new Date(utcMs(ymd)).getUTCDay()];
  return shortDate(ymd, todayYmd);
}

/**
 * The chips under the inline composer, in order: Assign to · Patient (only with a # patient) · Due ·
 * Repeats (only when the text repeats). A chip is `dim` when it shows a DEFAULT (nobody mentioned →
 * "You"; no date typed or picked → the default due day) and set (`dim: false`) when the draft
 * decided it.
 *
 * @param {object}   args
 * @param {Array<string|{name: string, isMe: boolean}>} [args.assigneeNames]
 *                   the @-mentioned people, in order (empty → "You", dim)
 * @param {string}   [args.meName]        the signed-in user's name — a STRING assignee equal to it
 *                   reads "You" (object entries carry their own isMe)
 * @param {string}   [args.patientName]   the #-tagged patient's display name, or '' / null
 * @param {string}   [args.dueYmd]        the due day the draft DECIDED ('YYYY-MM-DD': typed or
 *                   picked), or null
 * @param {string}   [args.defaultDueYmd] the caller's default due day (My day: today; Upcoming:
 *                   tomorrow), or null → today
 * @param {string}   args.todayYmd        today in the office zone, 'YYYY-MM-DD'
 * @param {string}   [args.repeatLabel]   myDay.js repeatLabel() text ('Repeats weekly on Monday'),
 *                   or '' — the leading "Repeats " is dropped (the chip's label says it)
 * @returns {Array<{key: 'assign'|'patient'|'due'|'repeat', label: string, value: string, dim: boolean}>}
 */
export function buildComposerChips({
  assigneeNames, meName, patientName, dueYmd, defaultDueYmd, todayYmd, repeatLabel,
} = {}) {
  const assignees = normalizeAssignees(assigneeNames, meName);
  const words = [];
  assignees.forEach((a) => {
    const w = assigneeWord(a);
    if (words.indexOf(w) === -1) words.push(w);
  });
  const chips = [{
    key: 'assign',
    label: 'Assign to',
    value: words.length ? words.join(', ') : 'You',
    dim: words.length === 0,
  }];

  const patient = String(patientName == null ? '' : patientName).trim();
  if (patient) chips.push({ key: 'patient', label: 'Patient', value: patient, dim: false });

  const decided = partsOf(dueYmd) ? dueYmd : null;
  const shown = decided || (partsOf(defaultDueYmd) ? defaultDueYmd : todayYmd);
  chips.push({ key: 'due', label: 'Due', value: dueChipLabel(shown, todayYmd), dim: !decided });

  const repeat = String(repeatLabel == null ? '' : repeatLabel).trim().replace(/^repeats\s+/i, '');
  if (repeat) chips.push({ key: 'repeat', label: 'Repeats', value: repeat, dim: false });

  return chips;
}

/**
 * The one-line note under the composer after adding a task the current view does NOT list
 * (design add(): "Added for Fri, Oct 9. You’ll find it under Upcoming."). '' when it is visible.
 *
 *   assigned to me (or to nobody → me)  "Added for <Fri, Oct 9>. You’ll find it under <where>."
 *        where = My day (due today) · Upcoming (due in 1–7 days) · Assigned to me (otherwise)
 *   assigned only to others             "Added for <Priya and Sam>. You’ll find it under Created by me."
 *   `where` never names the view the person is on (that view did not list it, e.g. a search was
 *   narrowing it): it falls back to Assigned to me, then Created by me / Everyone.
 *
 * @param {object}  args
 * @param {boolean} args.visibleInView  does the current view list the new task? true → ''
 * @param {string}  [args.dueYmd]       the task's due day, 'YYYY-MM-DD', or null ("no date")
 * @param {string}  args.todayYmd       today, 'YYYY-MM-DD' (the page's myDay.js ctx.today)
 * @param {Array<string|{name: string, isMe: boolean}>} [args.assigneeNames] the task's assignees
 * @param {string}  [args.meName]       the signed-in user's name (for STRING assignees)
 * @param {string}  [args.viewKey]      the current myDay.js view key, or null (a teammate / tag pick)
 * @returns {string}
 */
export function addedNote({ visibleInView, dueYmd, todayYmd, assigneeNames, meName, viewKey } = {}) {
  if (visibleInView) return '';
  const assignees = normalizeAssignees(assigneeNames, meName);
  const mine = assignees.length === 0 || assignees.some(a => a.isMe);
  const avoid = typeof viewKey === 'string' ? viewKey : null;
  const pick = keys => keys.find(k => k !== avoid) || keys[keys.length - 1];

  if (mine) {
    const due = partsOf(dueYmd) ? dueYmd : null;
    const diff = due ? daysBetween(todayYmd, due) : null;
    const first = diff === 0 ? 'myDay' : (diff != null && diff >= 1 && diff <= 7 ? 'upcoming' : 'assignedToMe');
    const where = pick([first, 'assignedToMe', 'createdByMe']);
    return `Added for ${due ? longDate(due) : 'no date'}. You’ll find it under ${NOTE_VIEW_LABELS[where]}.`;
  }
  const who = joinAnd(assignees.map(assigneeWord).filter((w, i, xs) => xs.indexOf(w) === i));
  const where = pick(['createdByMe', 'everyone']);
  return `Added for ${who}. You’ll find it under ${NOTE_VIEW_LABELS[where]}.`;
}

// ---- "N open" in the @ / # suggestion rows ------------------------------------------------------

const PATIENT_BADGE_RE = /data-teamio-patientid="([0-9]+)/g;

/**
 * Open-task counts behind the suggestion rows (design 2a: "3 open" beside each person, "2 open" /
 * "No open tasks" beside each patient).
 *
 * @param {object[]} tasks  the page's task list (ToDo.vue passes tkOpenAll). Only tasks with
 *                          status 'INCOMPLETE' count, so a mixed list is fine. Like myDay.js
 *                          combineTasks, id-less tasks are skipped and each id counts once.
 * @returns {{ staff: Object<string, number>, patients: Object<string, number> }}
 *   staff: String(EmployeeID) → open tasks the person is an assignee of: assignees[] ∪
 *          assignedToEmpID, once per task. Same rule as myDay.js teamCounts (the sidebar's Team
 *          counts), so the two numbers always agree; tasks-composer-chips.test.js pins that.
 *   patients: String(PatNum) → open tasks tagged with the patient: task.taggedPatients, or the
 *          patient badges in task.content for older tasks without taggedPatients. Once per task.
 */
export function openTaskCounts(tasks) {
  const staff = {};
  const patients = {};
  const seen = {};
  const bump = (map, keys) => Object.keys(keys).forEach((k) => { map[k] = (map[k] || 0) + 1; });
  (Array.isArray(tasks) ? tasks : []).forEach((t) => {
    if (!t || typeof t !== 'object' || t.id === undefined || t.id === null || t.id === '') return;
    if (seen[String(t.id)]) return;
    seen[String(t.id)] = true;
    if (t.status !== 'INCOMPLETE') return;

    const who = {};
    const addWho = (id) => { if (id !== undefined && id !== null && id !== '') who[String(id)] = true; };
    (Array.isArray(t.assignees) ? t.assignees : []).forEach(addWho);
    addWho(t.assignedToEmpID);
    bump(staff, who);

    const pats = {};
    const addPat = (id) => { if (id !== undefined && id !== null && id !== '') pats[String(id)] = true; };
    if (Array.isArray(t.taggedPatients) && t.taggedPatients.length) {
      t.taggedPatients.forEach(addPat);
    } else if (typeof t.content === 'string') {
      PATIENT_BADGE_RE.lastIndex = 0;
      let m;
      while ((m = PATIENT_BADGE_RE.exec(t.content)) !== null) addPat(m[1]);
    }
    bump(patients, pats);
  });
  return { staff, patients };
}

/**
 * The grey text at the right of a suggestion row.
 * @param {number} n       open tasks (missing / not a number → 0)
 * @param {'staff'|'patient'} kind
 * @returns {string} staff: "0 open", "3 open"; patient: "No open tasks", "2 open"
 */
export function openCountLabel(n, kind) {
  const c = Number(n) > 0 ? Math.floor(Number(n)) : 0;
  if (kind === 'patient' && c === 0) return 'No open tasks';
  return `${c} open`;
}
